import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CaseEdits } from './action-types';
import { api, apiWithCacheMeta } from '../core/net';
import {
  cmxDateTime,
  HttpMethods,
  codeBenchActions,
  appServiceLines
} from '@codametrix/ui-common';
import { patientDetailDictionaries } from '../reducers/state/case-edits';

const { fetchActivity } = codeBenchActions;

type UpdatedValue = {
  fieldKey: string;
  newValue: string;
};

const updateValue = createAction<UpdatedValue>(CaseEdits.UPDATE_VALUE);

interface loadPatientDetailsPayload {
  activeTenantId: string;
  facility: string;
  dictionary: string;
}

interface loadAllPatientDetailsPayload {
  activeTenantId: string;
  facility: string;
}

interface FieldOption {
  label: string;
  value: string;
}
interface FieldOptions {
  standardFields: FieldOption[];
  additionalFields: FieldOption[];
}

const loadPatientDetailOptions = createAsyncThunk<
  FieldOptions,
  loadPatientDetailsPayload,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseEdits.LOAD_PATIENT_DETAIL_OPTIONS, async (payload, _thunkAPI) => {
  const { activeTenantId, facility, dictionary } = payload;
  const dictionaryInfo = patientDetailDictionaries[dictionary];
  let fieldOptions: FieldOptions = {
    standardFields: [],
    additionalFields: []
  };
  try {
    if (dictionaryInfo) {
      let { dictionaryName, columnName } = dictionaryInfo as any;

      const searchParams = new URLSearchParams({
        tenantId: activeTenantId,
        derivedIndicator: 'true',
        effectiveDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        dictionaryName: dictionaryName as string,
        columnName: columnName as string
      });

      const res = await api<CMxAPI.RowData>({
        endpoint: `/dictionary/row/unrestricted/v2?${searchParams.toString()}`,
        init: {
          method: HttpMethods.GET
        }
      });

      const dictionaryFieldHandlers: {
        [key: string]: (row: { [key: string]: any }) => void;
      } = {
        Department: row => {
          fieldOptions.standardFields.push({
            label: row['Unit/Department'],
            value: row['Unit/Department ID']
          });
        },
        'Patient Class': row => {
          fieldOptions.standardFields.push({
            label: row[columnName],
            value: row[columnName]
          });
          fieldOptions.additionalFields.push({
            label: row['Patient Type'],
            value: row['Patient Class Original']
          });
        },
        'Note Specialty': row => {
          if (row?.['Facility'] === facility) {
            fieldOptions.standardFields.push({
              label: row[columnName],
              value: row[columnName]
            });
          }
        },
        default: row => {
          fieldOptions.standardFields.push({
            label: row[columnName],
            value: row[columnName]
          });
        }
      };

      res.rowValues.forEach((row: { [key: string]: any }) => {
        (
          dictionaryFieldHandlers[dictionaryName] ||
          dictionaryFieldHandlers['default']
        )(row);
      });
    }
  } catch (e) {
    console.error(e);
  }
  return fieldOptions;
});

const loadAllPatientDetailOptions = createAsyncThunk<
  void,
  loadAllPatientDetailsPayload,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseEdits.LOAD_ALL_PATIENT_DETAIL_OPTIONS, async (payload, { dispatch }) => {
  const { activeTenantId, facility } = payload;
  Object.keys(patientDetailDictionaries).forEach(dictionary => {
    dispatch(
      loadPatientDetailOptions({
        activeTenantId,
        facility,
        dictionary
      })
    );
  });
});

type UpdatedCaseFields = {
  caseId?: string | number;
  caseUid?: string;
  caseRunUid: number;
  serviceLine: string;
  caseUpdates: any;
  initialVal: any;
  mappedValues: any;
};

const updateCase = createAsyncThunk<
  void,
  UpdatedCaseFields,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseEdits.UPDATE_CASE, async (params, { dispatch }) => {
  const {
    caseId,
    caseUid,
    caseRunUid,
    serviceLine,
    caseUpdates,
    initialVal,
    mappedValues
  } = params;

  if (Object.keys(caseUpdates).includes('scheduling_department')) {
    const code = mappedValues.find((map: { label: string; value: string }) => {
      return map.label === caseUpdates['scheduling_department'];
    })?.value;

    caseUpdates['scheduling_department_code'] = code;
  }

  try {
    const updatedProcessInstance = await api<AmplifyAPI.ProcessInstance>({
      endpoint: `/coding-api/processinstance/${serviceLine}/${caseRunUid}/v1`,
      init: {
        method: HttpMethods.PUT,
        body: JSON.stringify(caseUpdates)
      }
    });

    if (caseId !== undefined || caseUid !== undefined) {
      const serviceLineConfig = appServiceLines.determineServiceLineConfig({
        name: serviceLine,
        caseIdentifier: `${caseUid ?? caseId}`
      });
      const caseIdentifier =
        serviceLineConfig.caseIdProp === 'case_uid' ? caseUid : caseId;
      if (caseIdentifier !== undefined) {
        const caseParams = new URLSearchParams(
          serviceLineConfig.caseDetailParms
        );

        try {
          await apiWithCacheMeta<CMxAPI.Case>(
            {
              endpoint: `${serviceLineConfig.path}/caseinstance/${caseIdentifier}/details/${
                serviceLineConfig.caseDetailVersion
              }?${caseParams.toString()}`,
              init: {
                method: HttpMethods.GET
              }
            },
            {
              acceptCache: false,
              cacheReplace: true
            }
          );
        } catch (error) {
          // The database update succeeded; cache replacement is best effort.
          console.warn('Unable to replace the case detail cache', error);
        }
      }
    }

    dispatch(fetchActivity(updatedProcessInstance) as any);
  } catch (e) {
    dispatch(updateValue(initialVal));
  }
});

export const caseEditsActions = {
  updateValue,
  loadPatientDetailOptions,
  loadAllPatientDetailOptions,
  updateCase
};
